import { DeviceAssignment } from "./PanelAssignment";
import { firestore } from "firebase/app";
import Panel, { getPanelTypeFromSerialNumber, PanelStatus, PanelType, StatusHistory } from "./Panel";
import {
  ComBox,
  getComBoxState,
  ComBoxDeviceState,
  getComBoxErrorFlags,
} from "./ComBox";
import { ProgramChain, doesProgramChainIncludeActiveRun, ProgramChainTimeRegionOfInterestOfPanel, ProgramChainTimeRegionOfInterestTypeOfPanel } from "./ProgramChain";
import { DevicePowerUsageType } from "./DeviceAttribute";
import ClientPanel from "./ClientPanel";
import { shouldHideSerialNumber } from "../repository/ClientRepository";

const OUTDATED_STATUS_THRESHOLD_IN_MS = 30 * 24 * 60 * 60 * 1000;

export const ONE_DAY_IN_MS = 24 * 60 * 60 * 1000;
export const IRES_TIMEOUT_THRESHOLD_WHILE_DRYING_IN_MS = 24 * 60 * 60 * 1000;
export const IRES_TIMEOUT_THRESHOLD_DEFAULT_IN_MS = 4 * 60 * 60 * 1000;

const MANUFACTURER = "IRES";
const ELECTRICITY_METER_MANUFACTURER = "EASTRON";

export enum IresDeviceIpuState {
  MALFUNCTION,
  WARNING,
  ACTIVE,
  IDLE,
  STANDBY,
  OTHER,
}

export interface IresDevice {
  serialNo: string;
  /**
   * When the record was created. Written by the production provisioning (the Raspberry Pi); older
   * records may not have it.
   */
  createdAt?: firestore.Timestamp;
  programChainData?: ProgramChainData;
  currentAssignment?: DeviceAssignment;
}

export interface IresDeviceStatus {
  createdAt: firestore.Timestamp;
  connectReason?: number;
  lastPingReceivedAt?: firestore.Timestamp;
  lastEvent: number;
  lastClientEvent?: number;
  source: IresDeviceStatusSource;
}

export interface MoveHyDryDevicesPayload {
  clientId: string;
  serialNumbers: string[];
  newClientId: string;
  enforceAssignmentDate: firestore.Timestamp;
  newBranchNumber?: string;
  isTemporaryAssignment?: boolean;
}

export enum IresDeviceGeneration {
  A,
  B,
  C,
  D,
  E,
  K,
}

export function getIresDeviceDisplayName(
  iresDevice: IresDevice,
  divider?: string,
  isClientDetailsSection?: boolean
) {
  const serialNo = iresDevice.serialNo;
  const inventoryNumber = iresDevice.currentAssignment?.inventoryNumber;

  if (shouldHideSerialNumber() || isClientDetailsSection) {
    return inventoryNumber;
  }

  if (!inventoryNumber || inventoryNumber === "" || inventoryNumber === serialNo) {
    return serialNo;
  }

  return `${inventoryNumber}${divider ?? " / "}${serialNo}`;
}

export function getDeviceTypeNameFromSerialNumber(vue: Vue, name: string) {
  const upperName = name.toUpperCase();
  const deviceTypeFlag = upperName.charAt(0);

  if (deviceTypeFlag == "B") {
    if (upperName.startsWith("BM")) {
      return vue.$t("iresDeviceType.mini").toString();
    }
    return vue.$t("iresDeviceType.box").toString();
  }

  if (deviceTypeFlag == "S") {
    return vue.$t("iresDeviceType.slim").toString();
  }

  if (deviceTypeFlag == "E") {
    return vue.$t("iresDeviceType.edge").toString();
  }

  if (deviceTypeFlag == "H") {
    return vue.$t("iresDeviceType.panel").toString();
  }

  return "";
}

export function getIresDeviceGeneration(iresDevice: IresDevice) {
  const upperSerial = iresDevice.serialNo.toUpperCase();

  // Mini devices (BMK*) are IPU generation K
  if (upperSerial.startsWith("BM")) {
    return IresDeviceGeneration.K;
  }

  const generationFlag = upperSerial.charAt(1);

  switch (generationFlag) {
    case "B":
      return IresDeviceGeneration.B;
    case "C":
      return IresDeviceGeneration.C;
    case "D":
      return IresDeviceGeneration.D;
    case "E":
      return IresDeviceGeneration.E;
    default:
      return IresDeviceGeneration.A;
  }
}

export function formatIresBatteryStatus(vue: Vue, batteryVoltageMV: number) {
  const batteryVoltageString = (
    batteryVoltageMV / 1000
  ).toLocaleString(undefined, { maximumFractionDigits: 3 });

  const batteryVoltageRatings = [
    3500,
    3400,
    3300,
    3200,
    3100,
    Number.MIN_VALUE,
  ];

  let rating = 1;
  let index = 0;

  while (batteryVoltageMV < batteryVoltageRatings[index++]) {
    rating++;
  }

  return vue
    .$t("iresDevice.formatBatteryStatus", [batteryVoltageString, rating])
    .toString();
}

export interface ProgramChainData {
  latestProgramChainNumber: number;
}

export enum IresDeviceStatusSource {
  IOT = "IOT",
  USB_OR_BLE = "USB_OR_BLE",
}

export enum IresDeviceCategory {
  PANEL,
  COMBOX,
}

export function getIresDeviceCategoryFromSerialNumber(serialNumber: string) {
  const upperCase = serialNumber.toUpperCase();
  return upperCase.startsWith("C") || upperCase.startsWith("B")
    ? IresDeviceCategory.COMBOX
    : IresDeviceCategory.PANEL;
}

export function getIresDeviceCategory(iresDevice: IresDevice) {
  return getIresDeviceCategoryFromSerialNumber(iresDevice.serialNo);
}

export function getIresDeviceCollectionName(serialNumber: string) {
  return getIresDeviceCategoryFromSerialNumber(serialNumber) ===
    IresDeviceCategory.PANEL
    ? "panels"
    : "comboxes";
}

export function getIresDeviceStatus(iresDevice: IresDevice) {
  const category = getIresDeviceCategory(iresDevice);

  if (category === IresDeviceCategory.PANEL) {
    const panel = iresDevice as Panel;
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    return panel.panelStatus!;
  } else {
    const comBox = iresDevice as ComBox;
    return comBox.comBoxStatus;
  }
}

export function isInStandBy(iresDevice: IresDevice) {
  const category = getIresDeviceCategory(iresDevice);

  if (category === IresDeviceCategory.PANEL) {
    return false;
  } else {
    const comBox = iresDevice as ComBox;
    return getComBoxState(comBox) === ComBoxDeviceState.STANDBY;
  }
}

export function didIresDeviceCommunicateInLast24Hours(
  iresDevice: IresDevice,
  now?: Date
) {
  const statusCreationTimestamp = getIresDeviceStatus(iresDevice)
    .createdAt.toDate()
    .getTime();

  const nowTimestamp = (now ?? new Date()).getTime();

  return Math.abs(statusCreationTimestamp - nowTimestamp) <= ONE_DAY_IN_MS;
}

export function getIresDeviceTimeoutThreshold(iresDevice: IresDevice): number {
  const category = getIresDeviceCategory(iresDevice);

  if (category === IresDeviceCategory.PANEL) {
    const panel = iresDevice as Panel;

    if (!panel.panelStatus) {
      return IRES_TIMEOUT_THRESHOLD_DEFAULT_IN_MS;
    }

    const panelStatus = panel.panelStatus;
    const isDrying = [1, 2, 3].includes(panelStatus.ipuStatus);

    return isDrying
      ? IRES_TIMEOUT_THRESHOLD_WHILE_DRYING_IN_MS
      : IRES_TIMEOUT_THRESHOLD_DEFAULT_IN_MS;
  } else {
    const comBox = iresDevice as ComBox;

    if (comBox.comBoxStatus.connectReason === 31) {
      //Power fail
      return IRES_TIMEOUT_THRESHOLD_DEFAULT_IN_MS;
    }

    return comBox.comBoxStatus.dryRunActive
      ? IRES_TIMEOUT_THRESHOLD_WHILE_DRYING_IN_MS
      : IRES_TIMEOUT_THRESHOLD_DEFAULT_IN_MS;
  }
}

export function isPoweredOff(iresDevice: IresDevice) {
  const category = getIresDeviceCategory(iresDevice);

  if (category === IresDeviceCategory.PANEL) {
    const panel = iresDevice as Panel;

    if (!panel.panelStatus) {
      return true;
    }

    if (panel.panelStatus.ipuStatus === 5) {
      return true;
    }

    const lastUpdate = panel.panelStatus.createdAt.toDate();
    const timeSinceLastUpdate = new Date().getTime() - lastUpdate.getTime();

    return timeSinceLastUpdate > getIresDeviceTimeoutThreshold(iresDevice);
  } else {
    const comBox = iresDevice as ComBox;

    // Sometimes, Josef needs dummy devices for testing, and for these devices, we don’t have the comBoxStatus field
    if (!comBox.comBoxStatus) {
      return true;
    }

    if (comBox.comBoxStatus.connectReason === 31) {
      return true;
    }

    const lastUpdate = comBox.comBoxStatus.createdAt.toDate();
    const timeSinceLastUpdate = new Date().getTime() - lastUpdate.getTime();

    return timeSinceLastUpdate > getIresDeviceTimeoutThreshold(iresDevice);
  }
}

export function didIresDeviceTimeOut(
  iresDevice: IresDevice,
  ignorePanelGeneration?: boolean
) {
  if (isPoweredOff(iresDevice)) {
    return false;
  }

  const now = new Date();
  const category = getIresDeviceCategory(iresDevice);

  if (category === IresDeviceCategory.PANEL) {
    const panel = iresDevice as Panel;

    if (!panel.panelStatus) {
      return false;
    }

    if (!panel.panelStatus?.createdAt) {
      return false;
    }

    const panelGeneration = getIresDeviceGeneration(panel);

    if (!ignorePanelGeneration && panelGeneration !== IresDeviceGeneration.D) {
      return false;
    }
  }

  const iresDeviceStatus = getIresDeviceStatus(iresDevice);

  if (!iresDeviceStatus) {
    return false;
  }

  const lastUpdate = iresDeviceStatus.createdAt.toDate();
  const timeSinceLastUpdate = now.getTime() - lastUpdate.getTime();

  return timeSinceLastUpdate > getIresDeviceTimeoutThreshold(iresDevice);
}

export function hasOutdatedStatus(iresDevice: IresDevice) {
  const generation = getIresDeviceGeneration(iresDevice);

  if (generation <= IresDeviceGeneration.C) {
    return false;
  }

  const category = getIresDeviceCategory(iresDevice);

  if (category === IresDeviceCategory.PANEL) {
    const panel = iresDevice as Panel;
    const panelStatus = panel.panelStatus;

    if (!panelStatus) {
      return true;
    }

    if (isPoweredOff(panel)) {
      return false;
    }

    const now = new Date();
    const lastUpdate = panelStatus.createdAt.toDate();

    const timeSinceLastUpdate = now.getTime() - lastUpdate.getTime();

    return timeSinceLastUpdate > OUTDATED_STATUS_THRESHOLD_IN_MS;
  } else {
    return didIresDeviceTimeOut(iresDevice);
  }
}

export function isDryRunFinnished(iresDevice: IresDevice) {
  const category = getIresDeviceCategory(iresDevice);

  if (category === IresDeviceCategory.PANEL) {
    const panel = iresDevice as Panel;
    return panel.panelStatus?.ipuStatus === 0;
  } else {
    const comBox = iresDevice as ComBox;
    const comBoxDeviceState = getComBoxState(comBox);

    return comBoxDeviceState === ComBoxDeviceState.DRY_RUN_ENDED;
  }
}

export function isIdle(iresDevice: IresDevice) {
  const category = getIresDeviceCategory(iresDevice);

  if (category === IresDeviceCategory.PANEL) {
    const panel = iresDevice as Panel;
    return panel.panelStatus?.ipuStatus === 0;
  } else {
    const comBox = iresDevice as ComBox;
    const comBoxDeviceState = getComBoxState(comBox);

    return (
      comBoxDeviceState === ComBoxDeviceState.IDLE ||
      comBoxDeviceState === ComBoxDeviceState.DRY_RUN_ENDED ||
      comBoxDeviceState === ComBoxDeviceState.STANDBY
    );
  }
}

export function hasWarning(iresDevice: IresDevice, doesSupportExtendedSensors = false) {
  const category = getIresDeviceCategory(iresDevice);

  if (category === IresDeviceCategory.PANEL) {
    const panel = iresDevice as Panel;
    return panel.panelStatus?.ipuStatus === 4;
  } else {
    const comBox = iresDevice as ComBox;
    // ==================== FIRMWARE v4.0.0+ CHANGES ====================
    return getComBoxErrorFlags(comBox.comBoxStatus, undefined, doesSupportExtendedSensors).some((flag) => flag === 30);
  }
}

export function hasError(iresDevice: IresDevice, doesSupportExtendedSensors = false) {
  const category = getIresDeviceCategory(iresDevice);

  if (category === IresDeviceCategory.PANEL) {
    const panel = iresDevice as Panel;
    return panel.panelStatus?.ipuStatus === 6;
  } else {
    const comBox = iresDevice as ComBox;
    // ==================== FIRMWARE v4.0.0+ CHANGES ====================
    return getComBoxErrorFlags(comBox.comBoxStatus, undefined, doesSupportExtendedSensors).some((flag) => flag === 31);
  }
}

export function calculateErrorOrWarningTimeIntervalsOfPanel(
  statusHistory: StatusHistory[],
  panelStatus?: PanelStatus
) {
  const timeRegionsOfErrorOrWarningOfPanel: ProgramChainTimeRegionOfInterestOfPanel[] = [];

  let lastIpuStatus = -1;
  let lastDate = new Date();

  let errorOrWarningStartDate = new Date();
  let errorOrWarningEndDate = new Date();
  
  let shouldAdd = false;
  let isError = false;
  let isWarning = false;

  if (panelStatus && statusHistory.length !== 0) {
    statusHistory.push({
      createdAt: panelStatus.createdAt,
      ipuStatus: panelStatus.ipuStatus
    });
  }

  if (statusHistory.length !== 0) {
    lastDate = statusHistory[0].createdAt.toDate();
    errorOrWarningStartDate = statusHistory[0].createdAt.toDate();
  }

  statusHistory.forEach((it) => {
    if (it.ipuStatus === 4 && lastIpuStatus !== 4) {
      errorOrWarningStartDate = it.createdAt.toDate();
      isWarning = true;
    }

    if (it.ipuStatus !== 4 && lastIpuStatus === 4) {
      errorOrWarningEndDate = lastDate;
      isWarning = true;
      shouldAdd = true;
    }

    if (it.ipuStatus === 6 && lastIpuStatus !== 6) {
      errorOrWarningStartDate = it.createdAt.toDate();
      isError = true;
    }

    if (it.ipuStatus !== 6 && lastIpuStatus === 6) {
      errorOrWarningEndDate = lastDate;
      isError = true;
      shouldAdd = true;
    }

    if (shouldAdd && (isError || isWarning)) {
      timeRegionsOfErrorOrWarningOfPanel.push({
        startDate: errorOrWarningStartDate,
        endDate: errorOrWarningEndDate,
        regionOfInterestType: isError ? ProgramChainTimeRegionOfInterestTypeOfPanel.HAS_ERROR : ProgramChainTimeRegionOfInterestTypeOfPanel.HAS_WARNING
      });

      shouldAdd = false;
      isError = false;
      isWarning = false;
      errorOrWarningStartDate = new Date();
      errorOrWarningEndDate = new Date();
    }

    lastDate = it.createdAt.toDate();
    lastIpuStatus = it.ipuStatus;
  });

  if (isError || isWarning) {
    timeRegionsOfErrorOrWarningOfPanel.push({
      startDate: errorOrWarningStartDate,
      endDate: errorOrWarningEndDate,
      regionOfInterestType: isError ? ProgramChainTimeRegionOfInterestTypeOfPanel.HAS_ERROR : ProgramChainTimeRegionOfInterestTypeOfPanel.HAS_WARNING
    });
  }

  timeRegionsOfErrorOrWarningOfPanel.sort((n1,n2) => {
    if (n1.startDate > n2.startDate) {
      return 1;
    }

    return -1;
  });

  let endDateOfLastErrorOrWarning = new Date();
  let startDateOfLastErrorOrWarning = new Date();
  let regionOfInterestOfLastErrorOrWarning: ProgramChainTimeRegionOfInterestTypeOfPanel | null = null;

  const indicesToBeRemoved: number[] = [];
  timeRegionsOfErrorOrWarningOfPanel.forEach((errorOrWarning, index) => {
    if (regionOfInterestOfLastErrorOrWarning === errorOrWarning.regionOfInterestType) {
      if (
        errorOrWarning.startDate >= startDateOfLastErrorOrWarning &&
        errorOrWarning.endDate <= endDateOfLastErrorOrWarning
      ) {
        indicesToBeRemoved.push(index);
      }
      else if (
        errorOrWarning.startDate <= startDateOfLastErrorOrWarning &&
        errorOrWarning.endDate >= endDateOfLastErrorOrWarning
      ) {
        indicesToBeRemoved.push(index - 1);
      }
      else if (endDateOfLastErrorOrWarning.toDateString() === errorOrWarning.startDate.toDateString()) {
        if (!indicesToBeRemoved.includes(index - 1)) {
          indicesToBeRemoved.push(index - 1);
        }
        errorOrWarning.startDate = startDateOfLastErrorOrWarning;
      }
    }

    regionOfInterestOfLastErrorOrWarning = errorOrWarning.regionOfInterestType;
    startDateOfLastErrorOrWarning = errorOrWarning.startDate;
    endDateOfLastErrorOrWarning = errorOrWarning.endDate;
  });

  indicesToBeRemoved
    .reverse()
    .forEach((index) => {
      timeRegionsOfErrorOrWarningOfPanel.splice(index, 1);
    });

  return timeRegionsOfErrorOrWarningOfPanel;
}

// ==================== FIRMWARE v4.0.0+ CHANGES ====================
export function isInWarningOrError(iresDevice: IresDevice, doesSupportExtendedSensors = false) {
  return hasWarning(iresDevice, doesSupportExtendedSensors) || hasError(iresDevice, doesSupportExtendedSensors);
}

// ==================== FIRMWARE v4.0.0+ CHANGES ====================
export function isInWarning(iresDevice: IresDevice, doesSupportExtendedSensors = false) {
  return hasWarning(iresDevice, doesSupportExtendedSensors);
}

// ==================== FIRMWARE v4.0.0+ CHANGES ====================
export function isInError(iresDevice: IresDevice, doesSupportExtendedSensors = false) {
  return hasError(iresDevice, doesSupportExtendedSensors);
}

export function isIresDeviceActive(iresDevice: IresDevice) {
  return (
    !isIdle(iresDevice) &&
    !isPoweredOff(iresDevice) &&
    !isInWarningOrError(iresDevice)
  );
}

function formatTimeDiffToNow(
  vue: Vue,
  timestamp: firestore.Timestamp,
  now: Date
) {
  const lastUpdateMillis = timestamp.toMillis();
  const nowMillis = now.getTime();

  const timeDiffInSeconds = (nowMillis - lastUpdateMillis) / 1000;

  let info = "";

  if (timeDiffInSeconds < 60) {
    info = vue.$t("panelDetail.lastInfo.current").toString();
  } else if (timeDiffInSeconds < 3600) {
    info = vue
      .$t("panelDetail.lastInfo.minutes", [Math.floor(timeDiffInSeconds / 60)])
      .toString();
  } else if (timeDiffInSeconds < 60 * 60 * 24) {
    info = vue
      .$t("panelDetail.lastInfo.hours", [Math.floor(timeDiffInSeconds / 3600)])
      .toString();
  } else {
    info = vue
      .$tc(
        "panelDetail.lastInfo.days",
        Math.floor(timeDiffInSeconds / (60 * 60 * 24))
      )
      .toString();
  }

  return info;
}

export function getLastUpdateText(
  vue: Vue,
  now: Date,
  status: IresDeviceStatus,
  showSource?: boolean,
  hideLastPing?: boolean
) {
  const infoLastPanelStatusUpdate = formatTimeDiffToNow(
    vue,
    status.createdAt,
    now
  );

  let result = "";

  if (showSource) {
    let sourceString = "";

    switch (status.source) {
      case IresDeviceStatusSource.IOT:
        sourceString = vue.$t("panelDetail.source.iot").toString();
        break;
      case IresDeviceStatusSource.USB_OR_BLE:
        sourceString = vue.$t("panelDetail.source.ble").toString();
        break;
    }
    result = `${sourceString} ${infoLastPanelStatusUpdate}`;
  } else {
    result = infoLastPanelStatusUpdate;
  }

  if (
    !hideLastPing &&
    status.lastPingReceivedAt &&
    status.lastPingReceivedAt.toMillis() > status.createdAt.toMillis()
  ) {
    const lastPingReceivedAtDeltaInMin = Math.floor(
      (now.getTime() - status.lastPingReceivedAt.toMillis()) / (1000 * 60)
    );

    const lastPing =
      lastPingReceivedAtDeltaInMin === 0
        ? vue.$t("panelDetail.lastInfo.lastPingCurrent").toString()
        : vue
            .$t("panelDetail.lastInfo.lastPing", [
              formatTimeDiffToNow(vue, status.lastPingReceivedAt, now),
            ])
            .toString();
    return `${result}\n(${lastPing})`;
  } else {
    return result;
  }
}

export function isDryingRunActive(iresDevice: IresDevice) {
  const iresDeviceCategory = getIresDeviceCategory(iresDevice);

  if (iresDeviceCategory === IresDeviceCategory.COMBOX) {
    return (iresDevice as ComBox).comBoxStatus.dryRunActive;
  } else {
    return !isPoweredOff(iresDevice);
  }
}

export function getIresDeviceIpuState(iresDevice: IresDevice) {
  if (hasError(iresDevice)) {
    return IresDeviceIpuState.MALFUNCTION;
  }

  if (hasWarning(iresDevice)) {
    return IresDeviceIpuState.WARNING;
  }

  if (isIresDeviceActive(iresDevice)) {
    return IresDeviceIpuState.ACTIVE;
  }

  if (isInStandBy(iresDevice)) {
    return IresDeviceIpuState.STANDBY;
  }

  if (isIdle(iresDevice)) {
    return IresDeviceIpuState.IDLE;
  }

  return IresDeviceIpuState.OTHER;
}

export function isActiveProgramChainOfIresDevice(
  iresDevice: IresDevice,
  programChain: ProgramChain
) {
  if (!iresDevice || !programChain) {
    return false;
  }

  const iresDeviceCategory = getIresDeviceCategory(iresDevice);

  if (iresDeviceCategory === IresDeviceCategory.COMBOX) {
    const comBox = iresDevice as ComBox;

    if (!comBox.comBoxStatus.dryRunActive) {
      return getComBoxState(comBox) === ComBoxDeviceState.WAITING;
    } else if (getComBoxState(comBox) === ComBoxDeviceState.POWER_FAIL) {
      const lastUpdate = comBox.comBoxStatus.createdAt.toDate();
      const timeSinceLastUpdate = new Date().getTime() - lastUpdate.getTime();

      if (timeSinceLastUpdate > getIresDeviceTimeoutThreshold(iresDevice)) {
        return false;
      }
    }

    return (
      programChain.runs[programChain.runs.length - 1]?.programRunID ===
        comBox.comBoxStatus.latestRunId ||
      programChain.runs[0]?.programRunID == comBox.comBoxStatus.dryRunFirstRunId
    );
  } else {
    const panel = iresDevice as Panel;
    if (!panel.panelStatus) {
      return false;
    }

    if (panel.panelStatus && isPoweredOff(iresDevice)) {
      const lastUpdate = panel.panelStatus.createdAt.toDate();
      const timeSinceLastUpdate = new Date().getTime() - lastUpdate.getTime();

      if (timeSinceLastUpdate > getIresDeviceTimeoutThreshold(iresDevice)) {
        return false;
      }
    }

    return doesProgramChainIncludeActiveRun(programChain);
  }
}

function isPanel(serialNumber: string) {
  return getIresDeviceCategoryFromSerialNumber(serialNumber) === IresDeviceCategory.PANEL;
}

export function getIresDevicePowerUsage(serialNumber: string) {
  return isPanel(serialNumber)
  ? DevicePowerUsageType.ELECTRICITY_METER
  : DevicePowerUsageType.ELECTRICITY_METER_FOR_OTHER_DEVICES
}

export function getIresDevicePowerUsageName(vue: Vue, serialNumber: string) {
  return isPanel(serialNumber)
  ? vue.$t("allDevices.electricityMeter").toString()
  : vue.$t("allDevices.electricityMeterForOtherDevices").toString()
}

export function getIresDeviceManufacturer() {
  return MANUFACTURER;
}

export function getIresDeviceElectricityMeterManufacturer() {
  return ELECTRICITY_METER_MANUFACTURER;
}

export function getIresDevicePowerConsumptionPerHour(serialNumber: string) {
  if (isPanel(serialNumber)) {
    switch (getPanelTypeFromSerialNumber(serialNumber)) {
      case PanelType.EDGE:
        return 820;
      case PanelType.SLIM:
        return 410;
      default:
        return 750;
    }
  }
  else {
    return 0
  }
}

export function mapUpdateToIresDevice(
  update: Partial<ClientPanel>,
  clientPanel: ClientPanel
): ClientPanel {
  Object.keys(update).forEach(key => {
    const castedKey = key as keyof ClientPanel;
    if (update[castedKey] !== undefined) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (clientPanel[castedKey] as any) = update[castedKey];
    }
  });

  return clientPanel;
}